import { useRef, useState, useEffect, useCallback } from "react";

interface LazyVideoProps {
  src: string;
  className?: string;
  onClick?: () => void;
}

export function LazyVideo({ src, className, onClick }: LazyVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && ref.current) {
      ref.current.load();
    }
  }, [isVisible, src]);

  const handleCanPlay = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.play().catch(() => {});
  }, []);

  const videoSrc = isVisible ? `${src}#t=0.1` : undefined;

  return (
    <video
      ref={ref}
      src={videoSrc}
      className={className}
      muted
      autoPlay
      loop
      playsInline
      preload={isVisible ? "auto" : "none"}
      onCanPlay={handleCanPlay}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      style={{ cursor: onClick ? "pointer" : undefined }}
    />
  );
}
